import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ChangeDetectorRef,
} from "@angular/core";
import { CartUploadService } from "../../../core/providers/cart-upload/cart-upload.service";
import { TemplateDownloadService } from "../../../core/providers/template-download/template-download.service";

@Component({
    selector: "vsf-upload-cart-modal",
    templateUrl: "./upload-cart-modal.component.html",
    styleUrls: ["./upload-cart-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadCartModalComponent {
    @Input() visible = false;
    @Input() isLoggedIn = true;
    @Input() consolidatedFeedPricingIds: number[] = [];
    @Input() consolidatedFeedImport = false;
    @Output() close = new EventEmitter<void>();
    @Output() uploadSuccess = new EventEmitter<{
        successfulCount: number;
        failedProductGtins: string[];
        moqViolations: Array<{ gtin: string; moq: number; quantity: number }>;
    }>();

    @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

    isDragOver = false;
    selectedFile: File | null = null;
    isUploading = false;
    uploadStatus: "idle" | "success" | "error" = "idle";
    errorMessage = "";
    successMessage = "";

    constructor(
        private cartUploadService: CartUploadService,
        private templateDownloadService: TemplateDownloadService,
        private cd: ChangeDetectorRef
    ) {}

    onCancel() {
        this.selectedFile = null;
        this.isUploading = false;
        this.uploadStatus = "idle";
        this.errorMessage = "";
        this.successMessage = "";
        this.close.emit();
        this.cd.markForCheck();
    }

    get isConsolidatedFeedImport(): boolean {
        return this.consolidatedFeedImport || this.consolidatedFeedPricingIds.length > 0;
    }

    async downloadTemplate() {
        try {
            if (this.isConsolidatedFeedImport) {
                this.downloadConsolidatedFeedTemplate();
                return;
            }
            await this.templateDownloadService.downloadTemplate();
        } catch (error: any) {
            console.error("Error downloading template:", error);
        }
    }

    private downloadConsolidatedFeedTemplate(): void {
        const csv = "EAN,Quantity,Offer ID\n";
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "Venduro consolidated feed cart upload.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    triggerFileInput() {
        this.fileInput.nativeElement.click();
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (!file) {
            return;
        }

        if (this.isValidFileType(file)) {
            this.selectedFile = file;
            this.uploadStatus = "idle";
            this.errorMessage = "";
        } else {
            this.selectedFile = null;
            this.uploadStatus = "error";
            this.errorMessage =
                "Unsupported file type. Please upload a .csv, .xls, or .xlsx file.";
        }

        input.value = "";
        this.cd.markForCheck();
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
        this.cd.markForCheck();
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        this.cd.markForCheck();
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (this.isValidFileType(file)) {
                this.selectedFile = file;
                this.uploadStatus = "idle";
                this.errorMessage = "";
            } else {
                this.selectedFile = null;
                this.uploadStatus = "error";
                this.errorMessage =
                    "Unsupported file type. Please upload a .csv, .xls, or .xlsx file.";
            }
        }
        this.cd.markForCheck();
    }

    async onUpload() {
        if (!this.selectedFile) return;

        this.isUploading = true;
        this.uploadStatus = "idle";
        this.errorMessage = "";
        this.successMessage = "";
        this.cd.markForCheck();

        try {
            const products = await this.cartUploadService.readCartUploadFile(
                this.selectedFile
            );

            const isConsolidatedFeedImport = this.isConsolidatedFeedImport;
            const result =
                await this.cartUploadService.addCheapestVariantsToCart(
                    products,
                    {
                        allowedPricingIds: isConsolidatedFeedImport
                            ? this.consolidatedFeedPricingIds
                            : undefined,
                        consolidatedFeedImport: isConsolidatedFeedImport,
                    },
                );

            this.uploadSuccess.emit({
                successfulCount: result.successfulCount,
                failedProductGtins: result.failedProductGtins,
                moqViolations: result.moqViolations,
            });

            this.isUploading = false;
            this.uploadStatus = "success";
            this.successMessage = `Successfully uploaded ${result.successfulCount} products to cart.`;

            if (result.failedProductGtins.length > 0) {
                this.successMessage += ` ${result.failedProductGtins.length} products could not be found.`;
            }

            this.cd.markForCheck();

            setTimeout(() => {
                if (this.uploadStatus === "success") {
                    this.onCancel();
                }
            }, 8000);
        } catch (error: any) {
            console.error("Error uploading file:", error);
            this.isUploading = false;
            this.uploadStatus = "error";
            this.errorMessage =
                error.message || "Failed to upload file. Please try again.";
            this.cd.markForCheck();
        }
    }

    getUploadButtonClass(): string {
        const baseClass =
            "w-full sm:w-auto px-4 py-3 sm:py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors touch-manipulation font-medium";

        if (this.uploadStatus === "success") {
            return `${baseClass} bg-green-600 hover:bg-green-700 focus:ring-green-500 text-white`;
        }

        return `${baseClass} bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 text-white disabled:opacity-50 disabled:cursor-not-allowed`;
    }

    getStatusClass(): string {
        if (this.isUploading) return "loading";
        if (this.uploadStatus === "success") return "success";
        if (this.uploadStatus === "error") return "error";
        if (
            this.selectedFile &&
            !this.isUploading &&
            this.uploadStatus === "idle"
        )
            return "confirmation";
        if (!this.selectedFile && this.uploadStatus === "idle")
            return "warning";
        return "";
    }

    getStatusIcon(): any {
        if (this.isUploading) return ["fas", "spinner"];
        if (this.uploadStatus === "success") return ["fas", "circle-check"];
        if (this.uploadStatus === "error") return ["fas", "exclamation-circle"];
        if (
            this.selectedFile &&
            !this.isUploading &&
            this.uploadStatus === "idle"
        )
            return ["fas", "file"];
        if (!this.selectedFile && this.uploadStatus === "idle")
            return ["fas", "exclamation-triangle"];
        return ["fas", "info-circle"];
    }

    getStatusMessage(): string {
        if (this.isUploading)
            return "⏳ Uploading... Please wait while we process your file.";
        if (this.uploadStatus === "success")
            return `✅ Upload Successful! ${this.successMessage}`;
        if (this.uploadStatus === "error")
            return `❌ Upload Failed ${this.errorMessage}`;
        if (
            this.selectedFile &&
            !this.isUploading &&
            this.uploadStatus === "idle"
        ) {
            return `File Ready for Upload: ${this.selectedFile.name}. Click "Upload cart" below to proceed.`;
        }
        if (!this.selectedFile && this.uploadStatus === "idle") {
            return this.isConsolidatedFeedImport
                ? "Products will be added to your cart using only consolidated feed channels."
                : "Products will be added to your cart.";
        }
        return "";
    }

    private isValidFileType(file: File): boolean {
        const name = file.name.toLowerCase();
        const validExtensions = [".csv", ".xls", ".xlsx"];

        if (validExtensions.some((ext) => name.endsWith(ext))) {
            return true;
        }

        const validTypes = [
            "text/csv",
            "application/csv",
            "text/comma-separated-values",
            "text/plain",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        return validTypes.includes(file.type);
    }
}
